import React, { useState, useRef } from 'react';
import { IconButton, Portal, Tooltip, Menu, MenuItem, ListItemIcon, Typography, Box, Button, alpha } from '@mui/material';
import {
  BugReport as BugIcon, Videocam as VideoIcon, CameraAlt as CameraIcon,
  TextFields as TextIcon, Stop as StopIcon, FiberManualRecord as RecIcon
} from '@mui/icons-material';
import BugReportDialog from './BugReportDialog';
import { useTranslation } from 'react-i18next';
import { fmtDecimal } from './ui/ledger';

const BugReportButton: React.FC = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [initialMedia, setInitialMedia] = useState<{ type: 'video' | 'screenshot' | 'none'; blob?: Blob; url?: string }>({ type: 'none' });

  // Recording state (lives outside dialog)
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const supportsScreenCapture = typeof navigator !== 'undefined' && !!(navigator.mediaDevices as any)?.getDisplayMedia;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (recording) return;
    setMenuAnchor(e.currentTarget);
  };

  const handleStartRecording = async () => {
    setMenuAnchor(null);
    try {
      const stream = await (navigator.mediaDevices as any).getDisplayMedia({
        video: { mediaSource: 'screen', frameRate: 24 },
        audio: true,
        preferCurrentTab: true
      });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm'
      });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecording(false);
        setRecordingTime(0);
        if (timerRef.current) clearInterval(timerRef.current);
        stream.getTracks().forEach((t: MediaStreamTrack) => t.stop());

        const maxSize = 10 * 1024 * 1024; // 10MB (backend multer limit)
        if (blob.size > maxSize) {
          const sizeMB = fmtDecimal(blob.size / (1024 * 1024), 1);
          alert(t('Video boyutu çok büyük ({{sizeMB}}MB). Lütfen daha kısa bir kayıt yapın veya ekran görüntüsü kullanın.', { sizeMB }));
          return;
        }

        const url = URL.createObjectURL(blob);
        setInitialMedia({ type: 'video', blob, url });
        setDialogOpen(true);
      };

      stream.getVideoTracks()[0].onended = () => {
        if (recorder.state === 'recording') recorder.stop();
      };

      recorder.start(1000);
      setRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 29) {
            recorder.stop();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);
    } catch {}
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const handleScreenshot = async () => {
    setMenuAnchor(null);
    try {
      const stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: false, preferCurrentTab: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;

      // Wait for actual video dimensions to be available
      await new Promise<void>(resolve => {
        video.onloadeddata = () => resolve();
        video.play();
      });

      // Use intrinsic video dimensions for pixel-perfect capture
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(video, 0, 0);

      stream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      video.remove();

      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
      if (blob) {
        const url = URL.createObjectURL(blob);
        setInitialMedia({ type: 'screenshot', blob, url });
        setDialogOpen(true);
      }
    } catch {}
  };

  const handleTextOnly = () => {
    setMenuAnchor(null);
    setInitialMedia({ type: 'none' });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setInitialMedia({ type: 'none' });
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <>
      {/* Recording floating bar (portalled: the header is its own stacking context) */}
      {recording && (
        <Portal>
        <Box sx={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5,
          borderRadius: 4, bgcolor: 'rgba(0,0,0,0.85)', color: 'white', zIndex: 9999,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          <RecIcon sx={{ fontSize: 14, color: '#f44336', animation: 'blink 1s infinite', '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
          <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', minWidth: 36 }}>
            {formatTime(recordingTime)}
          </Typography>
          <Button size="small" variant="contained" color="error" startIcon={<StopIcon />}
            onClick={handleStopRecording}
            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, py: 0.5 }}>
            {t('Kaydı Bitir')}
          </Button>
        </Box>
        </Portal>
      )}

      {/* Header button: lives in the top bar so it never covers page controls (chat composers, pagers) */}
      {!recording && (
        <Tooltip title={t('Hata Bildir')} arrow>
          <IconButton onClick={handleMenuOpen} aria-label={t('Hata bildir')}
            sx={{ width: 38, height: 38, borderRadius: 2, color: 'text.secondary', transition: 'background-color 150ms, color 150ms', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' } }}>
            <BugIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Action menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 200, boxShadow: `0 8px 24px ${alpha('#000', 0.15)}` } }}
      >
        {supportsScreenCapture && (
          <MenuItem onClick={handleStartRecording} sx={{ py: 1.5 }}>
            <ListItemIcon><VideoIcon sx={{ color: '#f44336' }} /></ListItemIcon>
            <Typography variant="body2" fontWeight={500}>{t('Ekran Kaydı')}</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={handleScreenshot} sx={{ py: 1.5 }}>
          <ListItemIcon><CameraIcon sx={{ color: '#1976D2' }} /></ListItemIcon>
          <Typography variant="body2" fontWeight={500}>{t('Ekran Görüntüsü')}</Typography>
        </MenuItem>
        <MenuItem onClick={handleTextOnly} sx={{ py: 1.5 }}>
          <ListItemIcon><TextIcon sx={{ color: '#757575' }} /></ListItemIcon>
          <Typography variant="body2" fontWeight={500}>{t('Sadece Yazı')}</Typography>
        </MenuItem>
      </Menu>

      {/* Dialog — opens AFTER media is captured */}
      <BugReportDialog open={dialogOpen} onClose={handleDialogClose} initialMedia={initialMedia} />
    </>
  );
};

export default BugReportButton;
